/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DateTime } from '@easepick/bundle';
import { BaseService } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import { Observable, of, switchMap } from 'rxjs';
import { EntityMetric, EntityMetricSeries } from './Entities';
import { EntityMetricStats } from './Entities/MetricStats/MetricStats.entity';
import { MetricCollectionServiceConfiguration } from './MetricCollection.service.config';
import {
  MetricCollectionServiceGetMetricDto,
  MetricCollectionServiceGetMetricSeriesDto,
  MetricCollectionServiceGetMetricStatsDto,
} from './MetricCollection.service.dto';

/**
 * The metric collection service.
 */
export class MetricCollectionService extends BaseService {
  public constructor(configuration: MetricCollectionServiceConfiguration) {
    super(configuration);
  }

  /**
   * Creates base api url for the Metric Collection service.
   */
  getBaseServiceUrl(): string | null {
    // This service is deployed in multiple regions and
    // needs to use the tenant's region.
    return this.getServiceUrlByTenant(`metrics`);
  }

  /**
   * Creates api url for getting a metric.
   */
  public createMetricUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/v1/metric`;
  }

  /**
   * Creates api url for getting a metric time series.
   */
  public createMetricSeriesUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/v1/metric/series`;
  }

  /**
   * Creates api url for getting a metric time series.
   */
  public createMetricStatsUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/v1/metric/stats`;
  }

  /**
   * Attempt to get a metric.
   */
  public getMetric(dto: MetricCollectionServiceGetMetricDto): Observable<EntityMetric | null> {
    const { metricClass, metricId, timestamp } = dto;

    const queryParams: Array<string> = [];
    queryParams.push(`class=${metricClass}`);

    if (metricId) {
      queryParams.push(`id=${metricId}`);
    }

    if (timestamp) {
      queryParams.push(`at=${timestamp}`);
    }

    const baseMetricUrl = this.createMetricUrl();
    if (!baseMetricUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseMetricUrl);
    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityMetric> => {
          return this._handleResponseError<EntityMetric>(value);
        }),
      );
  }

  /**
   * Attempt to get time series data for a metric.
   */
  public getMetricSeries(dto: MetricCollectionServiceGetMetricSeriesDto): Observable<EntityMetricSeries | null> {
    const { dateFrom, dateTo, metricClass, metricId, periodSize } = dto;

    const queryParams: Array<string> = [];
    queryParams.push(`class=${metricClass}`);
    queryParams.push(`dateFrom=${dateFrom}`);
    queryParams.push(`dateTo=${dateTo}`);

    if (metricId) {
      queryParams.push(`id=${metricId}`);
    }

    if (periodSize) {
      queryParams.push(`periodSize=${periodSize}`);
    }

    const baseMetricSeriesUrl = this.createMetricSeriesUrl();
    if (!baseMetricSeriesUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseMetricSeriesUrl);
    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityMetricSeries> => {
          return this._handleResponseError<EntityMetricSeries>(value);
        }),
      );
  }
  public getMetricStats(dto: MetricCollectionServiceGetMetricStatsDto): Observable<EntityMetricStats | null> {
    const { dateFrom, dateTo, host, range, type } = dto;
    const queryParams: Array<string> = [];
    queryParams.push(`datetimeFrom=${encodeURIComponent(new DateTime(dateFrom).toISOString())}`);
    queryParams.push(`datetimeTo=${encodeURIComponent(new DateTime(dateTo).toISOString())}`);
    queryParams.push(`host=${encodeURIComponent(host)}`);
    queryParams.push(`range=${range}`);
    type.forEach((t) => {
      queryParams.push(`type=${t}`);
    });

    const baseMetricSeriesUrl = this.createMetricStatsUrl();
    if (!baseMetricSeriesUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseMetricSeriesUrl);
    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityMetricStats> => {
          return this._handleResponseError<EntityMetricStats>(value);
        }),
      );
  }
}
